import React, { useState, useEffect } from "react";
import { X, Eye, EyeOff, ChevronDown, ChevronRight } from "lucide-react";

import ApiService from "../../../services/Api.service";

const AddUserModel = ({ onClose, onSave, initialData = null, title }) => {
  const [formData, setFormData] = useState({
    status: true,
    name: "",
    email: "",
    password: "",
    confirm_password: "",
    allowed_companies_list: [],
  });

  const [errors, setErrors] = useState({});
  const [dropDownData, setDropDownData] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  const isEditMode = Boolean(initialData);

  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData.id,
        status: initialData.status !== undefined ? initialData.status : true,
        name: initialData.name || "",
        email: initialData.email || "",
        password: "",
        confirm_password: "",
        allowed_companies_list: initialData.allowed_companies_list || [],
      });
    }
  }, [initialData]);

  const fetchDropdownDetail = async () => {
    try {
      const response = await ApiService.getCompanyOptions();

      if (response.status === 200) {
        setDropDownData(response.data.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDropdownDetail();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCompanySelect = (companyId) => {
    setFormData((prev) => {
      const companyValue = String(companyId);

      const selectedCompanies = prev.allowed_companies_list.map(String);

      const alreadySelected = selectedCompanies.includes(companyValue);

      return {
        ...prev,
        allowed_companies_list: alreadySelected
          ? selectedCompanies.filter((id) => id !== companyValue)
          : [...selectedCompanies, companyValue],
      };
    });

    if (errors.allowed_companies_list) {
      setErrors((prev) => ({
        ...prev,
        allowed_companies_list: "",
      }));
    }
  };

  const handleSave = () => {
    let newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name cannot be empty";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email cannot be empty";
    }

    if (!isEditMode && !formData.password) {
      newErrors.password = "Password cannot be empty";
    }

    if (!isEditMode && formData.password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    if (!formData.allowed_companies_list.length) {
      newErrors.allowed_companies_list = "Please select at least one company";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({
      id: initialData?.id,
      status: formData.status,
      name: formData.name,
      email: formData.email,
      password: formData.password,
      confirm_password: formData.confirm_password,
      allowed_companies_list: formData.allowed_companies_list,
    });

    onClose();
  };

  return (
    <div className="add-subparam-overlay">
      <div className="add-subparam-modal">
        <div className="add-subparam-header">
          <h3>{title || (isEditMode ? "Edit User" : "Add User")}</h3>

          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="add-subparam-body">
          <label style={{ marginTop: "10px" }}>Username</label>

          <input
            type="text"
            name="name"
            className="form-control"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter name"
          />

          {errors.name && <p className="edit-error">{errors.name}</p>}

          <label style={{ marginTop: "10px" }}>Email</label>

          <input
            type="email"
            name="email"
            className="form-control"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email"
          />

          {errors.email && <p className="edit-error">{errors.email}</p>}

          {!isEditMode && (
            <>
              <label style={{ marginTop: "10px" }}>Password</label>

              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="form-control"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                />

                <span
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </span>
              </div>

              {errors.password && (
                <p className="edit-error">{errors.password}</p>
              )}

              <label style={{ marginTop: "10px" }}>Confirm Password</label>

              <div className="password-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  className="form-control"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  placeholder="Confirm password"
                />

                <span
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={16} />
                  ) : (
                    <Eye size={16} />
                  )}
                </span>
              </div>

              {errors.confirm_password && (
                <p className="edit-error">{errors.confirm_password}</p>
              )}
            </>
          )}

          <div
            style={{
              border: "1px solid #ccc",
              borderRadius: "6px",
              marginTop: "15px",
              overflow: "hidden",
            }}
          >
            <div
              onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
              style={{
                padding: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                background: "#f8f8f8",
              }}
            >
              <span>
                {formData.allowed_companies_list.length > 0
                  ? `${formData.allowed_companies_list.length} Companies Selected`
                  : "Select Companies"}
              </span>

              {showCompanyDropdown ? (
                <ChevronDown size={18} />
              ) : (
                <ChevronRight size={18} />
              )}
            </div>

            {showCompanyDropdown && (
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  padding: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {dropDownData?.map((company, index) => {
                  const companyValue = String(company.value);

                  return (
                    <label
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        cursor: "pointer",
                        marginTop: 0,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.allowed_companies_list
                          .map(String)
                          .includes(companyValue)}
                        onChange={() => handleCompanySelect(companyValue)}
                      />

                      <span>{company.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {errors.allowed_companies_list && (
            <p className="edit-error">{errors.allowed_companies_list}</p>
          )}
        </div>

        <div className="add-subparam-footer">
          <button className="popup-cancel-btn" onClick={onClose}>
            Cancel
          </button>

          <button className="popup-add-btn" onClick={handleSave}>
            {isEditMode ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddUserModel;
